import { useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { RouteTypes } from '@energovizor/api-client';
import { useSelector } from 'react-redux';
import userSelector from '@/store/selectors/userSelector';
import { apiClient } from './apiClient';
import { useGetActiveMunicipality } from './getMunicipality';
import { ROUTES } from '@/routes';

export type ConsumptionDeviationAlert = NonNullable<RouteTypes['/buildings/consumption-deviation-alerts']['get']['response']['payload']>[number];
export type ConsumptionMonitoring = NonNullable<RouteTypes['/collection-points/:collectionPointId/consumption-monitoring']['get']['response']['payload']>;
export type ConsumptionMonitoringBody = RouteTypes['/collection-points/:collectionPointId/consumption-monitoring']['patch']['body'];
export const monitoringQueryKey = (municipalityId: string | null | undefined, collectionPointId: string) =>
  ['consumption-monitoring', municipalityId ?? null, collectionPointId] as const;

export const consumptionMonitoringPath = (buildingId: string, collectionPointId: string) =>
  ROUTES.CLIENT_CONSUMPTION_MONITORING.replace(':buildingId', buildingId).replace(':collectionPointId', collectionPointId);

export function useGetConsumptionMonitoring(collectionPointId: string, { enabled = true }: { enabled?: boolean } = {}) {
  const municipalityId = useGetActiveMunicipality({ enabled });
  return useQuery({
    queryKey: monitoringQueryKey(municipalityId, collectionPointId),
    enabled: enabled && !!municipalityId && !!collectionPointId,
    staleTime: 30_000,
    queryFn: async () => {
      const response = await apiClient.get('/collection-points/:collectionPointId/consumption-monitoring', { params: { collectionPointId } });
      if (!response.data.payload) throw new Error('MONITORING_SETTINGS_MISSING');
      return response.data.payload;
    },
  });
}

export const invalidateConsumptionMonitoring = (queryClient: QueryClient) => Promise.all([
  queryClient.invalidateQueries({ queryKey: ['consumption-deviation-alerts'] }),
  queryClient.invalidateQueries({ queryKey: ['consumption-monitoring'] }),
]);

export function useGetConsumptionDeviationAlerts({ buildingId, enabled = true }: { buildingId?: string; enabled?: boolean } = {}) {
  const user = useSelector(userSelector);
  const municipalityId = useGetActiveMunicipality({ enabled });
  return useQuery({
    queryKey: ['consumption-deviation-alerts', municipalityId ?? null, buildingId ?? 'all'],
    enabled: enabled && !!municipalityId && user?.type === 'client' && !!user.verifiedEmail && !!user.verifiedByAdmin,
    staleTime: 15_000,
    refetchInterval: 60_000,
    queryFn: async () => (await apiClient.get('/buildings/consumption-deviation-alerts', { params: { buildingId } })).data.payload ?? [],
  });
}

export const deviationBaselineLabels = {
  recent_weekdays: 'Medián posledních 4 stejných dnů v týdnu',
  seasonal_weekdays: 'Stejné dny v týdnu kolem stejného data minulého roku',
  same_month_last_year: 'Stejný měsíc minulého roku',
  historical_months: 'Medián všech předchozích dokončených měsíců',
  previous_year: 'Předchozí dokončené roční období',
};
export const deviationGranularityLabels = { daily: 'Denní', monthly: 'Měsíční', yearly: 'Roční' };
